import React, { useState } from "react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import useApi from "../../hooks/useApi";
import useContactMessages from "../../hooks/useContactMessages";
import { useEffect } from "react";
import ViewMessageModal from "./ViewMessageModal";

export default function ContactMessages() {
  const [page, setPage] = useState(1);
  const perPage = 20;
  const { data, isLoading, isError, error } = useContactMessages(page, perPage);
  const contactmessage = data?.data || [];
  const meta = data?.meta || {};
  const totalPages = meta.last_page || 1;

  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const handleView = (msg) => {
    setSelectedMessage(msg);
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  // Filter/search only on current page's data
  const filteredcontactmessage = contactmessage.filter((messages) => {
    const query = searchQuery.trim().toLowerCase();
    let matches = filterType === "all";
    if (filterType === "firstName") {
      matches = messages.firstName.toLowerCase().includes(query);
    } else if (filterType === "lastName") {
      matches = messages.lastName.toLowerCase().includes(query);
    } else if (filterType === "email") {
      matches = messages.email.toLowerCase().includes(query);
    } else if (filterType === "phone") {
      matches = messages.phone.toLowerCase().includes(query);
    } else if (filterType === "message") {
      matches = messages.message.toLowerCase().includes(query);
    }
    else if (filterType === "date") {
      matches = messages.created_at.toLowerCase().includes(query);
    }
    return matches;
  });

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };



  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary spinner-border-lg" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h5 className="mt-3 text-muted">Loading contact messages...</h5>
        <p className="text-muted small">Please wait while we fetch your messages</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="alert alert-danger alert-dismissible fade show shadow-sm" role="alert">
        <i className="ri-error-warning-line me-2"></i>
        <strong>Error:</strong> {error}
        <button type="button" className="btn-close" data-bs-dismiss="alert"></button>
      </div>
    );
  }

  return (
    <>
      <div className="card-header bg-white border-0 pt-4 pb-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
          <div className="mb-3 mb-md-0">
            <h4 className="mb-1 fw-bold text-primary">
              <i className="ri-mail-line me-2"></i>
              Contact Messages
            </h4>
            <p className="text-muted small mb-0">
              Showing {contactmessage.length} of {meta.total || 0} messages
              {searchQuery && ` • Filtered by "${searchQuery}"`}
            </p>
          </div>
          
          <div className="d-flex flex-wrap align-items-end gap-4">
            <div className="filter-group">
              <label className="form-label small text-muted mb-1 d-block">Filter by</label>
              <select
                className="form-select form-select-sm"
                style={{ minWidth: '150px' }}
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Messages</option>
                <option value="firstName">First Name</option>
                <option value="lastName">Last Name</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="message">Message</option>
                <option value="date">Date</option>
              </select>
            </div>
            
            <div className="search-group ms-auto">
              <label className="form-label small text-muted mb-1 d-block">Search</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light border-end-0">
                  <i className="ri-search-line"></i>
                </span>
                <input
                  type="text"
                  className={`form-control ${filterType === "all" ? "bg-light" : ""}`}
                  style={{ minWidth: '220px' }}
                  placeholder={filterType === "all" ? "Select filter type to search..." : `Search ${filterType.replace(/([A-Z])/g, ' $1').toLowerCase()}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={filterType === "all"}
                />
                {searchQuery && (
                  <button 
                    className="btn btn-outline-secondary" 
                    type="button"
                    onClick={() => setSearchQuery("")}
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body p-0">
                {filteredcontactmessage.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="empty-state">
                      <i className="ri-mail-open-line text-muted" style={{ fontSize: '4rem' }}></i>
                      <h5 className="mt-3 text-muted">No messages found</h5>
                      <p className="text-muted mb-4">
                        {searchQuery 
                          ? `No messages match your search "${searchQuery}"`
                          : "No contact messages available at the moment"}
                      </p>
                      {searchQuery && (
                        <button 
                          className="btn btn-outline-primary"
                          onClick={() => {
                            setSearchQuery("");
                            setFilterType("all");
                          }}
                        >
                          <i className="ri-refresh-line me-2"></i>
                          Clear filters
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th className="ps-4 text-muted fw-normal small" style={{ width: '80px' }}>
                              <i ></i>ID
                            </th>
                            <th className="text-muted fw-normal small">Name</th>
                            <th className="text-muted fw-normal small">Contact</th>
                            <th className="text-muted fw-normal small text-center" style={{ width: '120px' }}>Date</th>
                            <th className="text-muted fw-normal small">Message Preview</th>
                            <th className="text-muted fw-normal small text-center" style={{ width: '100px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredcontactmessage.map((msg, idx) => (
                            <tr key={idx} className="border-bottom border-light">
                              <td className="ps-4">
                                <span className="badge bg-light text-dark border">
                                  {msg.id}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <strong className="text-dark">
                                    {msg.firstName} {msg.lastName}
                                  </strong>
                                  {msg.subject && (
                                    <small className="text-muted">
                                      {msg.subject}
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <a href={`mailto:${msg.email}`} className="text-primary text-decoration-none">
                                    <i className="ri-mail-line me-1"></i>
                                    {msg.email}
                                  </a>
                                  {msg.phone && (
                                    <small className="text-muted mt-1">
                                      <i className="ri-phone-line me-1"></i>
                                      {msg.phone}
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td className="text-center">
                                <small className="text-muted">
                                  {msg.created_at ? new Date(msg.created_at).toLocaleDateString() : 'N/A'}
                                </small>
                              </td>
                              <td>
                                <div className="message-preview">
                                  <p className="text-truncate mb-0" style={{ maxWidth: '250px' }}>
                                    {msg.message}
                                  </p>
                                </div>
                              </td>
                              <td className="text-center">
                                <div className="btn-group btn-group-sm" role="group">
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => handleView(msg)}
                                    title="View Message"
                                  >
                                    <i className="ri-eye-line"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-success"
                                    onClick={() => window.location.href = `mailto:${msg.email}`}
                                    title="Reply"
                                  >
                                    <i className="ri-reply-line"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    title="Delete"
                                    onClick={() => {
                                      if (window.confirm('Are you sure you want to delete this message?')) {
                                        // Add delete functionality here
                                      }
                                    }}
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="card-footer bg-white border-0 pt-4 pb-3">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
                          <div className="mb-3 mb-md-0">
                            <p className="text-muted small mb-0">
                              Page {page} of {totalPages} • {meta.total || 0} total messages
                            </p>
                          </div>
                          
                          <nav aria-label="Page navigation">
                            <ul className="pagination pagination-sm mb-0">
                              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(page - 1)}
                                  disabled={page === 1}
                                >
                                  <i className="ri-arrow-left-s-line"></i> Previous
                                </button>
                              </li>
                              
                              {page > 3 && (
                                <li className="page-item">
                                  <button className="page-link" onClick={() => handlePageChange(1)}>
                                    1
                                  </button>
                                </li>
                              )}
                              
                              {page > 4 && (
                                <li className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              )}
                              
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum = page;
                                if (page <= 3) pageNum = i + 1;
                                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                                else pageNum = page - 2 + i;
                                
                                if (pageNum < 1 || pageNum > totalPages) return null;
                                
                                return (
                                  <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                                    <button className="page-link" onClick={() => handlePageChange(pageNum)}>
                                      {pageNum}
                                    </button>
                                  </li>
                                );
                              })}
                              
                              {page < totalPages - 3 && (
                                <li className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              )}
                              
                              {page < totalPages - 2 && (
                                <li className="page-item">
                                  <button className="page-link" onClick={() => handlePageChange(totalPages)}>
                                    {totalPages}
                                  </button>
                                </li>
                              )}
                              
                              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(page + 1)}
                                  disabled={page === totalPages}
                                >
                                  Next <i className="ri-arrow-right-s-line"></i>
                                </button>
                              </li>
                            </ul>
                          </nav>
                          
                          <div className="mt-3 mt-md-0">
                            <div className="input-group input-group-sm" style={{ width: '120px' }}>
                              <span className="input-group-text">Go to</span>
                              <input
                                type="number"
                                className="form-control"
                                min="1"
                                max={totalPages}
                                value={page}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value);
                                  if (val >= 1 && val <= totalPages) {
                                    handlePageChange(val);
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

      <ViewMessageModal
        show={showModal}
        handleClose={handleClose}
        message={selectedMessage}
      />

      <style jsx>{`
        .card {
          border-radius: 12px;
          transition: transform 0.2s ease;
        }
        
        .card:hover {
          transform: translateY(-2px);
        }
        
        .table th {
          border-top: none;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.75rem;
          color: #6c757d;
        }
        
        .table td {
          vertical-align: middle;
          padding: 1rem 0.75rem;
        }
        
        .table tbody tr {
          transition: background-color 0.15s ease;
        }
        
        .table tbody tr:hover {
          background-color: rgba(13, 110, 253, 0.04);
        }
        
        .message-preview {
          max-height: 60px;
          overflow: hidden;
          line-height: 1.4;
        }
        
        .badge {
          padding: 0.35em 0.65em;
          font-weight: 500;
          border-radius: 6px;
        }
        
        .empty-state {
          max-width: 400px;
          margin: 0 auto;
        }
        
        .page-item.active .page-link {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
        
        .page-link {
          color: #495057;
          border-radius: 6px;
          margin: 0 2px;
          min-width: 40px;
          text-align: center;
        }
        
        .btn-group .btn {
          border-radius: 6px !important;
          margin: 0 1px;
        }
        
        .input-group-text {
          background-color: #f8f9fa;
          border-color: #dee2e6;
        }
        
        @media (max-width: 768px) {
          .table-responsive {
            font-size: 0.875rem;
          }
          
          .btn-group .btn {
            padding: 0.25rem 0.5rem;
          }
          
          .filter-group, .search-group {
            width: 100%;
          }
          
          .filter-group select,
          .search-group .input-group {
            width: 100% !important;
          }
        }
      `}</style>
    </>
  );
}